import React, { useState, useEffect } from 'react';
import { X, Check, Clock, Image as ImageIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Frame {
  url: string;
  timestamp: number;
  timeString: string;
}

interface FrameSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  frames: Frame[];
  stepId: string;
  stepTitle: string;
  currentFrames: string[]; // URLs of frames currently in this step
  onSelectFrames: (stepId: string, frameUrls: string[]) => void;
}

const FrameSelector: React.FC<FrameSelectorProps> = ({
  isOpen,
  onClose,
  frames,
  stepId,
  stepTitle,
  currentFrames,
  onSelectFrames
}) => {
  const [selectedFrames, setSelectedFrames] = useState<Set<string>>(new Set(currentFrames));

  useEffect(() => {
    if (isOpen) {
      setSelectedFrames(new Set(currentFrames));
    }
  }, [isOpen, currentFrames]);

  const toggleFrame = (frameUrl: string) => {
    const newSelected = new Set(selectedFrames);
    if (newSelected.has(frameUrl)) {
      newSelected.delete(frameUrl);
    } else {
      newSelected.add(frameUrl);
    }
    setSelectedFrames(newSelected);
  };

  const handleApply = () => {
    onSelectFrames(stepId, Array.from(selectedFrames));
    onClose();
  };

  const handleClear = () => {
    setSelectedFrames(new Set());
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Select Screenshots</h2>
              <p className="text-sm text-gray-600 mt-1">
                Choose screenshots for: <span className="font-semibold">{stepTitle}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Frame Grid */}
          <div className="flex-1 overflow-y-auto p-6">
            {frames.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No frames available</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {frames.map((frame, index) => {
                  const isSelected = selectedFrames.has(frame.url);
                  return (
                    <motion.div
                      key={frame.url}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                        isSelected
                          ? 'border-blue-500 ring-2 ring-blue-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => toggleFrame(frame.url)}
                    >
                      {/* Frame Image */}
                      <div className="aspect-video bg-gray-100 relative">
                        <img
                          src={frame.url}
                          alt={`Frame at ${frame.timeString}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23ddd" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage not found%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        
                        {/* Overlay on hover */}
                        <div className={`absolute inset-0 bg-black transition-opacity ${
                          isSelected ? 'bg-opacity-20' : 'bg-opacity-0 group-hover:bg-opacity-30'
                        }`} />
                        
                        {/* Timestamp Badge */}
                        <div className="absolute top-2 left-2 flex items-center space-x-1 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">
                          <Clock className="w-3 h-3" />
                          <span>{frame.timeString}</span>
                        </div>
                        
                        {/* Selection Indicator */}
                        {isSelected && (
                          <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1">
                            <Check className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                      
                      {/* Frame Info */}
                      <div className="p-2 bg-white">
                        <p className="text-xs text-gray-600 truncate">
                          Frame {index + 1} • {frame.timeString}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {selectedFrames.size} of {frames.length} selected
              </span>
              {selectedFrames.size > 0 && (
                <button
                  onClick={handleClear}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Clear Selection
                </button>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center space-x-2"
              >
                <Check className="w-4 h-4" />
                <span>Apply ({selectedFrames.size})</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default FrameSelector;

